import { Play, Pause, Volume2 } from 'lucide-react';
import { useRef, useState, useCallback, type FC } from 'react';

interface AudioPlayerProps {
  url: string | null | undefined;
}

const AudioPlayer: FC<AudioPlayerProps> = ({ url }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
    setPlaying(!playing);
  }, [playing]);

  const onTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setProgress((audio.currentTime / audio.duration) * 100);
  }, []);

  const onLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio) setDuration(audio.duration);
  }, []);

  const onEnded = useCallback(() => {
    setPlaying(false);
    setProgress(0);
  }, []);

  const seek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const pct = (e.clientX - rect.left) / rect.width;
      audio.currentTime = pct * duration;
    },
    [duration],
  );

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (!url) {
    return (
      <div className="fc-card p-4">
        <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
          <Volume2 className="h-4 w-4" />
          <span className="text-xs font-medium uppercase tracking-wider">Audio</span>
        </div>
        <p className="mt-2 text-sm italic text-slate-400 dark:text-slate-500">
          Pas d&apos;enregistrement audio.
        </p>
      </div>
    );
  }

  return (
    <div className="fc-card p-4">
      <div className="mb-3 flex items-center gap-2 text-slate-500 dark:text-slate-400">
        <Volume2 className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-wider">Audio</span>
      </div>

      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        onEnded={onEnded}
        preload="metadata"
      />

      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-700"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
        </button>

        <div className="flex flex-1 flex-col gap-1">
          {/* Progress bar */}
          <div
            className="h-1.5 w-full cursor-pointer rounded-full bg-slate-200 dark:bg-slate-600"
            onClick={seek}
          >
            <div
              className="h-full rounded-full bg-blue-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500">
            <span>{fmt((progress / 100) * duration)}</span>
            <span>{duration ? fmt(duration) : '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
